/**
 * Owner: Masa Bayu
 * File: AdminAccountScreen.tsx
 * Date:Time: 2026-10-23 11:55
 * Description: §468-D — skrin akaun bagi sesi pentadbir. Chrome pembaca
 *   (`ReaderProfileScreen`) DIBUANG untuk peranan ini: tiada My Library, tiada
 *   Orders & Licenses, tiada wishlist / Continue reading, tiada "Become an
 *   author" dan tiada "Cancel account" — akaun operasi rasmi tidak memegang hak
 *   bacaan dan tidak boleh membatalkan dirinya sendiri.
 *
 *   Gantiannya ialah insight pengurusan: navigasi lima seksyen konsol, KPI
 *   daripada papar-masa `GET /v1/admin/dashboard`, dan rekod sesi (ID akaun,
 *   titik akhir hub, kaedah log masuk, masa papar-masa) + log keluar. Kelas
 *   `qxa__*` sedia ada dipakai supaya skrin ini satu keluarga dengan konsol.
 */

import { useEffect, useMemo } from 'react';
import type { ReactElement } from 'react';
import { getHubAuthAccountId } from '../publication/hub-auth';
import { qxpHubOrigin } from '../publication/hub-sync';
import { getMarketplaceLocaleProfile } from '../home/marketplace/marketplace-locale';
import type { AuthSessionView } from '../auth/session-types';
import { ADMIN_NAV } from './AdminConsole';
import { ADMIN_LABELS } from './admin-labels';
import { ADMIN_RESTRICTED_LABELS } from './admin-restricted-labels';
import { refreshAdminDashboard, useAdminDashboard } from './admin-dashboard';
import { NO_DATA, overviewKpis, signInLabel } from './admin-view';
import { Icon } from './AdminIcons';
import './admin-console.css';
import './admin-restricted.css';

export interface AdminAccountScreenProps {
  readonly authSession: AuthSessionView;
  readonly onNavigate: (path: string) => void;
  readonly onSignOut: () => void;
}

function monogramOf(name: string, email: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);
  const first = parts[0] ?? '';
  if (parts.length === 0) {
    const initial = email.slice(0, 1).toUpperCase();
    return initial.length > 0 ? initial : '?';
  }
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  return `${first.slice(0, 1)}${(parts[1] ?? '').slice(0, 1)}`.toUpperCase();
}

function flagOf(session: AuthSessionView, key: 'hasPassword' | 'googleLinked'): boolean {
  return (session as { readonly [k: string]: unknown })[key] === true;
}

export function AdminAccountScreen({
  authSession,
  onNavigate,
  onSignOut,
}: AdminAccountScreenProps): ReactElement {
  const language = getMarketplaceLocaleProfile().language;
  const la = ADMIN_RESTRICTED_LABELS[language];
  const ga = ADMIN_LABELS[language];
  const dash = useAdminDashboard();

  useEffect(() => {
    void refreshAdminDashboard();
  }, []);

  const account = authSession.account;
  const displayName = account?.profile.displayName || account?.user.email || '';
  const email = account?.user.email ?? '';
  const view = dash.view;
  const kpis = useMemo(() => (view === null ? [] : overviewKpis(view, ga)), [view, ga]);
  const signIn = signInLabel(
    {
      hasPassword: flagOf(authSession, 'hasPassword'),
      googleLinked: flagOf(authSession, 'googleLinked'),
    },
    ga,
  );

  return (
    <section className="qxa qxa--restricted qxa-acct" aria-labelledby="qxa-acct-title">
      <header className="qxa-acct__hero">
        <span className="qxa-acct__avatar" aria-hidden="true">
          {monogramOf(displayName, email)}
        </span>
        <div className="qxa-acct__herobody">
          <h1 id="qxa-acct-title" className="qxa-acct__name">
            {displayName}
          </h1>
          {email !== '' ? <p className="qxa-acct__email">{email}</p> : null}
          <span className="qxa-acct__badge">
            <Icon name="shield" size={13} /> {la.acctBadge}
          </span>
        </div>
        <button
          type="button"
          className="qxa__btn qxa__btn--primary"
          onClick={() => {
            onNavigate('/admin');
          }}
        >
          <Icon name="overview" size={16} />
          <span>{la.acctConsoleTitle}</span>
        </button>
      </header>

      <div className="qxa-acct__grid">
        <section className="qxa__card" aria-label={la.acctConsoleTitle}>
          <div className="qxa__card-head">
            <h2 className="qxa__card-title">{la.acctConsoleTitle}</h2>
          </div>
          <p className="qxa__sub">{la.acctConsoleBody}</p>
          <ul className="qxa-acct__nav">
            {ADMIN_NAV.map((item) => (
              <li key={item.id}>
                <span className="qxa-acct__navlabel">
                  <Icon name={item.id} size={17} />
                  {ga.nav[item.id]}
                </span>
                <button
                  type="button"
                  className="qxa__btn"
                  onClick={() => {
                    onNavigate(item.path);
                  }}
                >
                  <span>{la.acctOpenConsole}</span>
                  <Icon name="arrowRight" size={15} />
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="qxa__card" aria-label={la.acctInsightTitle}>
          <div className="qxa__card-head">
            <h2 className="qxa__card-title">{la.acctInsightTitle}</h2>
          </div>
          <p className="qxa__sub">{la.acctInsightBody}</p>
          {view === null ? (
            <p className="qxa__note">{dash.status === 'loading' ? NO_DATA : la.acctWaiting}</p>
          ) : (
            <ul className="qxa__kpis qxa__kpis--dense">
              {kpis.map((kpi) => (
                <li key={kpi.id} className="qxa__kpi">
                  <p className="qxa__kpi-label">{kpi.label}</p>
                  <p className="qxa__kpi-value">{kpi.value}</p>
                  {kpi.sub === undefined ? null : <p className="qxa__kpi-sub">{kpi.sub}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="qxa__card" aria-label={la.acctSessionTitle}>
          <div className="qxa__card-head">
            <h2 className="qxa__card-title">{la.acctSessionTitle}</h2>
          </div>
          <p className="qxa__sub">{la.acctSessionBody}</p>
          <dl className="qxa-acct__facts">
            <div>
              <dt>{la.acctAccountId}</dt>
              <dd>{getHubAuthAccountId() ?? account?.user.id ?? NO_DATA}</dd>
            </div>
            <div>
              <dt>{la.acctHub}</dt>
              <dd>{qxpHubOrigin()}</dd>
            </div>
            <div>
              <dt>{la.acctSignInMethod}</dt>
              <dd>{signIn}</dd>
            </div>
            <div>
              <dt>{la.acctSnapshot}</dt>
              <dd>
                {view === null
                  ? NO_DATA
                  : `${view.generatedAt.slice(0, 19).replace('T', ' ')} · ${
                      view.actor === 'token' ? ga.actorToken : view.actor
                    }`}
              </dd>
            </div>
          </dl>
          <button
            type="button"
            className="qxa__btn qxa-acct__signout"
            onClick={() => {
              onSignOut();
              onNavigate('/');
            }}
          >
            <Icon name="logout" size={16} />
            <span>{la.acctSignOut}</span>
          </button>
        </section>
      </div>

      <p className="qxa__note qxa-acct__note">{la.acctNoLibraryNote}</p>
    </section>
  );
}
